<template>
  <div class="dish-display-container fade-in" >   
    <!-- 头部区域 -->
    <div class="dish-header">
      <h1>{{ shop_name }}</h1>
      <!-- 价格标签区（支持点击切换+高亮） -->
      <div class="price-tags">
        <div
          v-for="(tag, index) in priceTags"
          :key="tag.type"
          class="price-tag"
          :class="{ active: currentDishType === tag.type }"
          @click="switchDishType(tag.type)"
        >
          {{ tag.name }}
        </div>
      </div>
    </div>

    <!-- 主体内容（轮播 + 菜品列表） -->
    <div class="dish-main-content">
      <!-- 菜品轮播区（基于当前显示的菜品区切换） -->
      <div class="dish-carousel-container">
        <el-carousel
          v-if="currentDishesWithImages.length > 0"
          :interval="4000"
          indicator-position="outside"
          height="350px"
          class="custom-carousel"
        >
          <el-carousel-item
            v-for="(dish, index) in currentDishesWithImages"
            :key="'carousel-' + currentDishType + '-' + index"
          >
            <img
              :src="dish.image"
              :alt="dish.name"
              class="carousel-image"
              @error="handleImgError($event)"
            />
          </el-carousel-item>
        </el-carousel>
        <div v-else class="no-dishes">
          暂无{{ currentDishType === "5yuan" ? "5元" : "3元" }}菜品图片
        </div>
      </div>

      <!-- 菜品信息列表区（基于当前显示的菜品区切换） -->
      <div class="dish-info-container">
        <div
          class="dish-list"
          ref="dishList"
          @mouseenter="stopScrolling"
          @mouseleave="startScrolling"
        >
          <!-- 无当前价位菜品提示 -->
          <div v-if="currentDishes.length === 0" class="no-dishes">
            暂无{{
              currentDishType === "5yuan" ? "5元" : "3元"
            }}菜品，请联系工作人员！
          </div>
          <!-- 当前菜品区列表 -->
          <div v-else ref="dishItemsContainer">
            <div
              class="dish-item"
              v-for="(dish, index) in currentDishes"
              :key="'dish-' + currentDishType + '-' + index"
            >
              <div class="dish-info">
                <div class="dish-name">{{ dish.name }}</div>
                <div class="dish-details">
                  <div class="dish-detail">
                    蛋白质：<span>{{ dish.protein || "暂无" }}</span>
                  </div>
                  <div class="dish-detail">
                    脂肪：<span>{{ dish.fat || "暂无" }}</span>
                  </div>
                  <div class="dish-detail">
                    碳水化合物：<span>{{ dish.carbs || "暂无" }}</span>
                  </div>
                  <div class="dish-detail">
                    能量：<span>{{ dish.calories || "暂无" }}</span>
                  </div>
                </div>
              </div>
              <div class="dish-price">
                <div class="price">{{ dish.price ? dish.price : "¥0.00" }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 引入混入
import { myMixin } from "../common/common.js";
export default {
  name: "DishDisplay",
  mixins: [myMixin],
  props: {
    title: {
      type: String,
      default: "菜品展示",
    },
    subtitle: {
      type: String,
      default: "今日推荐",
    },
    // 父组件传入的原始菜品数组（包含5元和3元菜品）
    dishes: {
      type: Array,
      required: true,
      default: () => [],
    },
    footerText: {
      type: String,
      default: "",
    },
    scrollSpeed: {
      type: Number,
      default: 1, // 滚动速度，数值越大越快
    },
    scrollInterval: {
      type: Number,
      default: 30, // 滚动间隔时间(ms)
    },
    scrollPauseAtEnd: {
      type: Number,
      default: 800, // 滚动到底部后切换前的短暂停留
    },
  },
  data() {
    return {
      scrollTimer: null,
      isScrolling: false,
      currentDishType: "5yuan", // 当前显示的菜品区：5yuan/3yuan
      // 固定价格标签配置
      priceTags: [
        { name: "5元菜品区", type: "5yuan" },
        { name: "3元菜品区", type: "3yuan" },
      ],
    };
  },
  computed: {
    // 过滤5元菜品区数据
    dishes5Yuan() {
      return this.dishes.filter((dish) => {
        const priceStr = (dish.price || "").toString();
        return priceStr.includes("5") && !priceStr.includes("3");
      });
    },
    // 过滤3元菜品区数据
    dishes3Yuan() {
      return this.dishes.filter((dish) => {
        const priceStr = (dish.price || "").toString();
        return priceStr.includes("3") && !priceStr.includes("5");
      });
    },
    // 当前显示的菜品列表
    currentDishes() {
      return this.currentDishType === "5yuan"
        ? this.dishes5Yuan
        : this.dishes3Yuan;
    },
    // 当前菜品区的轮播图数据
    currentDishesWithImages() {
      return this.currentDishes
        .filter((dish) => dish.image)
        .map((dish) => ({ ...dish }));
    },
    // 判断当前菜品区是否需要滚动
    needScroll() {
      if (!this.$refs.dishList || !this.$refs.dishItemsContainer) return false;
      return (
        this.$refs.dishItemsContainer.offsetHeight >
        this.$refs.dishList.offsetHeight
      );
    },
  },
  methods: {
    handleImgError(e) {
      e.target.src = "https://via.placeholder.com/400x300?text=暂无图片";
    },

    // 切换菜品区（点击标签触发）
    switchDishType(targetType) {
      if (this.currentDishType === targetType) return;

      this.currentDishType = targetType;

      this.$nextTick(() => {
        if (this.$refs.dishList) {
          this.$refs.dishList.scrollTop = 0;
        }
        if (this.isScrolling) {
          this.stopScrolling();
          this.startScrolling();
        }
      });
    },

    // 开始滚动
    startScrolling() {
      if (this.currentDishes.length <= 0 || this.isScrolling) return;
      if (!this.needScroll) return;

      this.isScrolling = true;

      const scroll = () => {
        if (!this.isScrolling) return;

        const dishList = this.$refs.dishList;
        const container = this.$refs.dishItemsContainer;

        const isAtBottom =
          dishList.scrollTop + dishList.offsetHeight >=
          container.offsetHeight - 5;

        if (isAtBottom) {
          // 滚动到底部：切换到另一个菜品区
          setTimeout(() => {
            if (this.isScrolling) {
              this.currentDishType =
                this.currentDishType === "5yuan" ? "3yuan" : "5yuan";
              dishList.scrollTop = 0;
              this.scrollTimer = setTimeout(scroll, this.scrollInterval);
            }
          }, this.scrollPauseAtEnd);
        } else {
          // 正常向下滚动
          dishList.scrollTop += this.scrollSpeed;
          this.scrollTimer = setTimeout(scroll, this.scrollInterval);
        }
      };

      this.scrollTimer = setTimeout(scroll, this.scrollInterval);
    },

    // 停止滚动（鼠标悬停时触发）
    stopScrolling() {
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = null;
      }
      this.isScrolling = false;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.startScrolling();
    });
  },
  beforeDestroy() {
    this.stopScrolling();
  },
  watch: {
    dishes() {
      this.$nextTick(() => {
        this.stopScrolling();
        this.startScrolling();
      });
    },
  },
};
</script>

<style scoped>
.dish-display-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 20px;
}

.dish-header {
  display: flex;
  color: #303133;
  padding: 20px 30px 10px 30px;
  text-align: center;
  position: relative;
  align-items: center;
}

.dish-header h1 {
  font-size: 28px;
  margin-bottom: 8px;
  letter-spacing: 1px;
  flex: 1;
  text-align: left;
}

/* 价格标签样式 */
.price-tags {
  position: static;
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
}

.price-tag {
  background: #f0f2f5;
  color: #303133;
  padding: 8px 18px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

/* 高亮样式 */
.price-tag.active {
  background: #409eff;
  color: #ffffff;
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
  transform: translateY(-1px);
}

.price-tag:not(.active):hover {
  background: #e5e6eb;
  border-color: #dcdfe6;
}

.dish-main-content {
  display: flex;
  padding: 0;
}

.dish-carousel-container {
  flex: 1;
  padding: 25px;
  border-right: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.dish-info-container {
  border-left: 2px dashed #dcdfe6;
  flex: 1;
  padding: 25px 25px 25px 35px;
  position: relative;
  overflow: hidden;
}

.dish-list {
  height: 380px;
  overflow-y: auto;
  padding-right: 10px;
}

/* 隐藏滚动条但保留滚动功能 */
.dish-list::-webkit-scrollbar {
  width: 6px;
}

.dish-list::-webkit-scrollbar-track {
  background: transparent;
}

.dish-list::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.dish-item {
  display: flex;
  justify-content: space-between;
  padding: 20px;
  margin-bottom: 15px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.dish-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.dish-info {
  flex: 1;
}

.dish-name {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.dish-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.dish-detail {
  color: #606266;
  font-size: 14px;
}

.dish-detail span {
  color: #409eff;
  font-weight: 500;
}

.dish-price {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  min-width: 120px;
  padding-left: 20px;
  border-left: 1px dashed #dcdfe6;
}

.price {
  color: #f56c6c;
  font-size: 24px;
  font-weight: bold;
}

.no-dishes {
  text-align: center;
  padding: 30px;
  color: #909399;
  font-size: 16px;
}

.custom-carousel {
  width: 100%;
  overflow: hidden;
}

.custom-carousel .el-carousel__container {
  height: 350px;
  border-radius: 8px;
}

.custom-carousel .el-carousel__item {
  background: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 响应式设计 */
@media (max-width: 900px) {
  .dish-main-content {
    flex-direction: column;
  }

  .dish-carousel-container {
    border-right: none;
    border-bottom: 1px solid #ebeef5;
  }

  .custom-carousel .el-carousel__container {
    height: 500px;
  }

  .dish-item {
    flex-direction: column;
  }

  .dish-price {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0 0 0;
    border-left: none;
    border-top: 1px dashed #dcdfe6;
    margin-top: 15px;
    min-width: 100%;
  }

  .dish-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .price-tags {
    margin-left: 0;
    margin-top: 10px;
    width: 100%;
    justify-content: flex-start;
  }
}

/* 渐入动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 0.8s ease;
}
</style>
